import {
  markVisualOptions,
  resetVisualOptions,
} from "./lianxi-yufa-visual-options.js";
import { supportedText } from "./lianxi-yufa-supported-text.js";
import { focusedGroup } from "./lianxi-yufa-focused.js";
import { fillGap, resetGap } from "./lianxi-yufa-gap.js";
import { inRow, onLine, takeBack, tiles } from "./fuxi-tile-moves.js";
import {
  isArrangement,
  isChoosing,
  optionsOf,
  readAnswer,
  requirePart,
  typedInput,
} from "./lianxi-yufa-item.js";
import { clearOptions, markOptions } from "./lianxi-xuanze.js";
import { clearPlaced } from "./lianxi-yufa-placing.js";
import type { ItemAnswer } from "../fuxi/yufa-marking.js";

/**
 * Putting one item of a grammar exercise 语法练习 back as the markup drew
 * it, saying what its answer was, and placing the caret.
 *
 * The showing half of `./lianxi-yufa-item.ts`, which reads an item's answer
 * off the same names. Three ways of answering: tiles on a line for an
 * arrangement, a sentence picked for a Choose item, and a field for a gap
 * or a Transform item.
 */

/** Clear what the reader answered, whichever way the item is answered. */
function clearAnswer(form: HTMLFormElement): void {
  const group = focusedGroup(form);
  if (form.dataset["cbYufaKind"] === "repair") {
    const input = typedInput(form);
    input.value = "";
    input.readOnly = false;
    input.disabled = false;
    input.removeAttribute("aria-invalid");
  }
  if (isChoosing(form)) {
    clearOptions(optionsOf(form));
    clearPlaced(form);
  } else if (group !== null) {
    group.value = null;
    group.disabled = false;
  } else if (isArrangement(form)) {
    const card = form.querySelector(tiles)!;

    for (const placed of [...card.querySelectorAll(onLine)].toReversed()) {
      takeBack(card, placed, false);
    }
  } else {
    const input = typedInput(form);

    input.value = "";
    input.readOnly = false;
    input.removeAttribute("aria-invalid");
  }
}

/**
 * Show one item and hide the rest.
 * @param items - Every item, in the exercise's order.
 * @param at - The item to show. Nothing hides them all.
 */
export function showOnly(
  items: readonly HTMLFormElement[],
  at: number | undefined,
): void {
  for (const [index, form] of items.entries()) {
    form.hidden = index !== at;
  }
}

/**
 * Put the item back as the markup drew it: nothing answered, the marks
 * hidden and Check showing.
 * @param form - The item.
 */
export function resetItem(form: HTMLFormElement): void {
  form
    .querySelector<HTMLElement>("[data-cb-fuxi-tiles]")
    ?.removeAttribute("inert");
  resetGap(form);
  resetVisualOptions(form);
  delete form.dataset["cbYufaAnswered"];
  delete form.dataset["cbYufaVerdict"];
  clearAnswer(form);
  requirePart(form, "mark").textContent = "";
  const followup = form.querySelector<HTMLElement>("[data-cb-yufa-followup]");
  if (followup !== null) {
    followup.textContent = "";
  }
  requirePart(form, "why").hidden = true;
  // An item of options is answered by its first press, and has no Check to
  // show (CB-130).
  requirePart(form, "check").hidden = isChoosing(form);
  requirePart(form, "next").hidden = true;
}

/**
 * The event an item fires once it is marked, which `lianxi-yufa-sounds.ts`
 * plays the sentence on.
 */
export const yufaMarked = "cb-lianxi:yufa-marked";

/**
 * Say what the answer was, show the right sentence whole, and offer the way
 * on.
 * @param form - The item.
 * @param line - What to say about the answer.
 * @param passed - Whether the answer passed, which a typed field says too.
 * @param unverified - Whether the answer has no checked grammatical verdict.
 */
export function markItemOnPage(
  form: HTMLFormElement,
  line: string,
  passed: boolean,
  unverified = false,
): void {
  const group = focusedGroup(form);
  if (isChoosing(form)) {
    markChosen(form);
  } else if (group !== null) {
    group.disabled = true;
    markVisualOptions(form);
  }
  if (
    (group === null || form.dataset["cbYufaKind"] === "repair") &&
    !isArrangement(form) &&
    !isChoosing(form)
  ) {
    if (unverified) {
      typedInput(form).removeAttribute("aria-invalid");
    } else {
      typedInput(form).setAttribute("aria-invalid", passed ? "false" : "true");
    }
    typedInput(form).readOnly = true;
  }

  fillGap(form, passed);
  form.dataset["cbYufaAnswered"] = "";
  form.dataset["cbYufaVerdict"] = unverified
    ? "unverified"
    : passed
      ? "right"
      : "wrong";
  form
    .querySelector<HTMLElement>("[data-cb-fuxi-tiles]")
    ?.setAttribute("inert", "");
  supportedText(form, requirePart(form, "mark"), line);
  const why = requirePart(form, "why");
  // A reviewed explanation often says what the line under the answer says,
  // and the page says it once (CB-23).
  const reason = why.querySelector<HTMLElement>(".lianxi-yufa-reason");
  if (reason !== null) {
    reason.hidden = line.includes(reason.textContent.trim());
  }
  why.hidden = false;
  requirePart(form, "check").hidden = true;

  const next = requirePart(form, "next");

  next.hidden = false;
  next.focus();
  form.dispatchEvent(
    new CustomEvent(yufaMarked, {
      bubbles: true,
      detail: { passed, unverified },
    }),
  );
}

/** The values an answer chosen from options is right with. */
function rightValues(answer: ItemAnswer | undefined): readonly number[] {
  if (answer?.kind === "choose") {
    return [answer.right];
  }

  return answer?.kind === "contrast" ||
    answer?.kind === "placement" ||
    answer?.kind === "interpretation" ||
    answer?.kind === "reply"
    ? answer.accepted
    : [];
}

/**
 * Ring an item's right options and a wrong one chosen, as a reading
 * Exercise's question is marked, and leave the rest to fade.
 */
function markChosen(form: HTMLFormElement): void {
  const radios = optionsOf(form);
  const chosen = radios.find((radio) => radio.checked);

  if (chosen === undefined) {
    return;
  }

  const right = rightValues(readAnswer(form));

  markOptions(radios, (radio) => right.includes(Number(radio.value)), chosen);
}

/**
 * Put the caret where the reader answers: the first tile, the first
 * sentence, or the field.
 * @param form - The item.
 */
export function focusAnswer(form: HTMLFormElement): void {
  const group = focusedGroup(form);
  if (isChoosing(form)) {
    // The letters choose from anywhere on the page, so the caret is left on
    // the item rather than on the first option, which would read as chosen.
    form.setAttribute("tabindex", "-1");
    form.focus({ preventScroll: true });
  } else if (group !== null) {
    group.focus();
  } else if (isArrangement(form)) {
    form.querySelector<HTMLElement>(`${inRow}:not([disabled])`)?.focus();
  } else {
    typedInput(form).focus();
  }
}
